import { formatNumberWithCommas } from 'common/utils/common.util';
import { Calculation, ExportPDFOptions } from '../../components/construction-cost-report/construction-cost-report.type';
import { CustomTableLayout } from 'pdfmake/interfaces';
import { Content } from 'pdfmake/interfaces';
import DejaVuSans_Regular_Base64 from '../../assets/font/DejaVuSans_Regular_Base64';
import Roboto from '../../assets/font/Roboto';

export const setupFontsForPdfMake = () => {
  // Assign the virtual file system for fonts and add custom fonts to it
  window.pdfMake.vfs = {
    'Roboto-Regular.ttf': Roboto['Roboto-Regular.ttf'],
    'Roboto-Medium.ttf': Roboto['Roboto-Medium.ttf'],
    'Roboto-Italic.ttf': Roboto['Roboto-Italic.ttf'],
    'Roboto-MediumItalic.ttf': Roboto['Roboto-MediumItalic.ttf'],
    'DejaVuSans-Regular.ttf': DejaVuSans_Regular_Base64,
  };

  // Define custom fonts
  window.pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
    DejaVuSans: {
      normal: 'DejaVuSans-Regular.ttf',
      bold: 'DejaVuSans-Regular.ttf',
      italics: 'DejaVuSans-Regular.ttf',
      bolditalics: 'DejaVuSans-Regular.ttf',
    },
  };
};

export const grayHeaderLineLayout: CustomTableLayout = {
  hLineWidth: (i: number, node: any): number => {
    if (i === 0 || i === node.table.body.length) {
      return 0;
    } else if (i === 1) {
      return 1;
    } else {
      return 0;
    }
  },
  vLineWidth: (i: number): number => {
    return 0;
  },
  hLineColor: (i: number): string => {
    return i === 1 ? '#808080' : 'transparent';
  },
  paddingTop: (i: number): number => {
    return 4;
  },
  paddingBottom: (i: number): number => {
    return 4;
  },
};

export const renderCalculations = (calculations: Calculation[], isProjection: boolean, heading: string, width?: number | '*'): Content => {
  const avgCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'Average')?.budget.toFixed(2) || '0'));
  const highCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'High')?.budget.toFixed(2) || '0'));
  const lowCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'Low')?.budget.toFixed(2) || '0'));

  // Format numbers with commas
  const formattedAvgCalculation = formatNumberWithCommas(avgCalculation.toString());
  const formattedHighCalculation = formatNumberWithCommas(highCalculation.toString());
  const formattedLowCalculation = formatNumberWithCommas(lowCalculation.toString());

  if (avgCalculation === 0 && highCalculation === 0 && lowCalculation === 0) {
    return {
      stack: [
        {
          table: {
            widths: [width || '*'],
            heights: [41.6],
            body: [
              [{ text: heading.toLocaleUpperCase(), style: 'header', alignment: 'center' }],
              [{ text: 'No data available', alignment: 'center', fontSize: 6, margin: [0, 0, 0, 10] }],
              [
                {
                  columns: [
                    {
                      text: ` `,
                      style: ['low', 'grayText'],
                      alignment: 'right',
                      margin: [0, 0, 1, 0],
                      noWrap: true,
                      fontSize: 6,
                      width: '*',
                    },
                    { text: ' ', alignment: 'center', style: 'dot', margin: [0, 0, 0, 0], width: 7 },
                    {
                      text: ` `,
                      style: ['high', 'grayText'],
                      alignment: 'left',
                      margin: [1, 0, 0, 0],
                      noWrap: true,
                      fontSize: 6,
                      width: '*',
                    },
                  ],
                },
              ],
            ],
          },
          layout: getTableLayout(),
          margin: [0, 0, 3, 0], // Add margin to separate the tables,
          alignment: 'center',
        },
      ],
    };
  }

  return {
    stack: [
      {
        table: {
          widths: [width || '*'],
          body: [
            [{ text: heading.toLocaleUpperCase(), style: 'header', alignment: 'center' }],
            [{ text: `$${formattedAvgCalculation}`, style: 'average', alignment: 'center', noWrap: true, font: 'DejaVuSans' }],
            [
              {
                columns: [
                  {
                    text: `$${formattedLowCalculation} \u2193`,
                    style: ['low', 'grayText'],
                    alignment: 'right',
                    margin: [0, 0, 1, 0],
                    noWrap: true,
                    fontSize: 6,
                    width: '*',
                    font: 'DejaVuSans',
                  },
                  { text: '•', alignment: 'center', style: 'dot', margin: [0, 0, 0, 0], width: 7 },
                  {
                    text: `\u2191 $${formattedHighCalculation}`,
                    style: ['high', 'grayText'],
                    alignment: 'left',
                    margin: [1, 0, 0, 0],
                    noWrap: true,
                    fontSize: 6,
                    width: '*',
                    font: 'DejaVuSans',
                  },
                ],
                margin: [0, 0, 0, 10],
              },
            ],
          ],
        },
        layout: getTableLayout(),
        margin: [0, 0, 3, 0], // Add margin to separate the tables,
        alignment: 'center',
      },
    ],
  };
};

const getTableLayout = () => ({
  hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.5 : 0),
  vLineWidth: (i: number, node: any) => (i === 0 || i === node.table.widths?.length ? 0.5 : 0),
  hLineColor: () => 'gray',
  vLineColor: () => 'gray',
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 3,
});

const getEmptyTableBody = (heading: string, width?: number | '*') => [
  [{ text: heading.toLocaleUpperCase(), style: 'header', alignment: 'center' }],
  [{ text: 'No data available', alignment: 'center', fontSize: 6 }],
  [
    {
      columns: [
        { text: ' ', style: ['low', 'grayText'], alignment: 'right', margin: [0, 0, 1, 0], noWrap: true, fontSize: 6, width: '*' },
        { text: ' ', alignment: 'center', style: 'dot', margin: [0, 0, 0, 0], width: 7 },
        { text: ' ', style: ['high', 'grayText'], alignment: 'left', margin: [1, 0, 0, 0], noWrap: true, fontSize: 6, width: '*' },
      ],
    },
  ],
];

export const calculateWidth = (options: ExportPDFOptions): number | '*' => {
  const selectedOptionsCount = [options.includeCostPerGSF, options.includeCostPerNSF, options.includeCostPerUnit].filter(
    option => option,
  ).length;

  return selectedOptionsCount === 3 ? 150 : selectedOptionsCount === 2 ? 234 : 487;
};
